import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Typo } from 'uikit/content';

import { DraggableListItem } from '@/nestedRows';
import {
  getDataFromDomElement,
  getIsCourseReadOnly,
  useGlobalQueries,
  useIsLabFeatureEnabled,
  useObjectiveableIdFromUrl,
} from '@/exceed';

import getModalLearningObjective from '../helpers/getModalLearningObjective';
import handleDeleteLearningObjective from '../helpers/handleDeleteLearningObjective';

import useIsSourceMaterialProcessed from '../hooks/useIsSourceMaterialProcessed';
import useLearningObjectives from '../hooks/useLearningObjectives';

import AiGenerateLearningObjectives from './aiGenerateLearningObjectives';
import AiGenerateLearningObjectivesChat from './aiGenerateLearningObjectivesChat';
import LearningObjectivesList from './learningObjectivesList';
import LearningObjectivesListItemActions from './learningObjectivesListItemActions';
import LearningObjectivesListItemContent from './learningObjectivesListItemContent';
import LearningObjectivesListItemDragHandle from './learningObjectivesListItemDragHandle';
import LearningObjectivesListItemKebabMenu from './learningObjectivesListItemKebabMenu';
import LearningObjectivesListItemToggleExpandButton from './learningObjectivesListItemToggleExpandButton';

import styles from '../styles/learningObjectives.module.scss';

const LearningObjectives = ({
  defaultObjectiveableId = null,
  draftState = 'editing',
  isCourseDraft = true,
}) => {
  const objectiveableIdFromUrl = useObjectiveableIdFromUrl();
  const objectiveableId = useMemo(
    () => defaultObjectiveableId || objectiveableIdFromUrl,
    [defaultObjectiveableId, objectiveableIdFromUrl],
  );

  const dataFromDomElement = getDataFromDomElement('data-course-ai-learning-objectives');
  const hasAiGenerateLearningObjectives =
    useIsLabFeatureEnabled('learning_objectives') ||
    dataFromDomElement?.courseAiLearningObjectives === 'true';
  const isAiGenerateLearningObjectivesChat =
    hasAiGenerateLearningObjectives &&
    dataFromDomElement?.courseAiLearningObjectivesStrategy === 'chat';
  const objectiveableParentId = dataFromDomElement.courseCurriculumId;

  const {
    addGeneratedLearningObjective,
    deleteLearningObjective,
    generateLearningObjectives,
    learningObjectives,
    mutateLearningObjective,
    orderedLearningObjectives,
    refetchLearningObjectives,
    reorderLearningObjectives,
    toggleVisibility,
  } = useLearningObjectives({
    isCourseDraft,
    objectiveableId,
    objectiveableParentId,
  });
  const isSourceMaterialProcessed = useIsSourceMaterialProcessed({ objectiveableId });
  const isReadOnly = getIsCourseReadOnly({ draftState, isCourseDraft });

  const handleActionItemClick = (action) => {
    switch (action) {
      case 'add':
        getModalLearningObjective({
          action,
          data: {
            objectiveable_id: objectiveableId,
            objectiveable_type: 'Course',
            ai_generated: false,
          },
          mutateLearningObjective,
        });
        break;
      case 'show':
        toggleVisibility(true);
        break;
      case 'hide':
        toggleVisibility(false);
        break;
      default:
        break;
    }
  };

  const handleKebabMenuItemClick = ({ action, item }) => {
    switch (action) {
      case 'edit':
        getModalLearningObjective({
          action,
          item,
          mutateLearningObjective,
        });
        break;
      case 'delete':
        handleDeleteLearningObjective({ item, deleteLearningObjective });
        break;
      default:
        break;
    }
  };

  const renderListItem = (props) => {
    const Actions = !isReadOnly ? LearningObjectivesListItemActions : () => null;

    return (
      <DraggableListItem
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        className={styles.learningObjectivesListItem}
        isKebabDisabled={isReadOnly}
        isAddButtonDisabled={isReadOnly}
        isDragDisabled={isReadOnly}
        onKebabMenuItemClick={handleKebabMenuItemClick}
        onActionItemClick={handleActionItemClick}
        onExpand={() => null}
        onCollapse={() => null}
        components={{
          Actions,
          Content: LearningObjectivesListItemContent,
          DragHandle: LearningObjectivesListItemDragHandle,
          KebabMenu: LearningObjectivesListItemKebabMenu,
          ToggleExpandButton: LearningObjectivesListItemToggleExpandButton,
        }}
      />
    );
  };

  const renderCustomPlaceholder = ({ placeholderProps }) => (
    <div
      className="placeholder"
      style={{
        position: 'absolute',
        top: placeholderProps.clientY,
        paddingLeft: placeholderProps.clientX,
        width: placeholderProps.clientWidth,
        background: 'rgba(49, 104, 246, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
        paddingTop: '18px',
        paddingBottom: '18px',
      }}
    >
      <div>
        <Typo variant="b01" element="span">
          {placeholderProps.clientItem.data.content}
        </Typo>
      </div>
    </div>
  );

  const { adminSettings, learnerSettings } = useGlobalQueries();
  const learningObjectivesCount = learningObjectives.data?.learningObjectives?.data?.length;
  const isLearningObjectivesDisplayed =
    learningObjectives.data?.learningObjectives?.meta?.display_learning_objectives || false;
  const shouldShowLoadingState =
    adminSettings.isLoading || learnerSettings.isLoading || learningObjectives.isLoading;
  const shouldShowEmptyState =
    !shouldShowLoadingState &&
    learningObjectivesCount === 0 &&
    learningObjectives.isSuccess;
  const shouldShowNestedRows =
    !shouldShowLoadingState &&
    learningObjectives.isSuccess &&
    learningObjectivesCount > 0;

  if (isAiGenerateLearningObjectivesChat) {
    return (
      <AiGenerateLearningObjectivesChat
        channelName={learningObjectives.data?.learningObjectives?.meta?.socket_channel}
        handleActionItemClick={handleActionItemClick}
        handleKebabMenuItemClick={handleKebabMenuItemClick}
        isReadOnly={isReadOnly}
        isLearningObjectivesDisplayed={isLearningObjectivesDisplayed}
        isSourceMaterialProcessed={isSourceMaterialProcessed}
        renderCustomPlaceholder={renderCustomPlaceholder}
        renderListItem={renderListItem}
        objectiveableId={objectiveableId}
        orderedLearningObjectives={orderedLearningObjectives}
        refetchLearningObjectives={refetchLearningObjectives}
        reorderLearningObjectives={reorderLearningObjectives}
        shouldShowEmptyState={shouldShowEmptyState}
        shouldShowNestedRows={shouldShowNestedRows}
        shouldShowLoadingState={shouldShowLoadingState}
      />
    );
  }

  if (hasAiGenerateLearningObjectives) {
    return (
      <AiGenerateLearningObjectives
        addGeneratedLearningObjective={addGeneratedLearningObjective}
        channelName={learningObjectives.data?.learningObjectives?.meta?.socket_channel}
        generateLearningObjectives={generateLearningObjectives}
        generationStatus={learningObjectives.data?.learningObjectives?.meta?.most_recent_ai_generation_status}
        handleActionItemClick={handleActionItemClick}
        handleKebabMenuItemClick={handleKebabMenuItemClick}
        isReadOnly={isReadOnly}
        isLearningObjectivesDisplayed={isLearningObjectivesDisplayed}
        isSourceMaterialProcessed={isSourceMaterialProcessed}
        renderCustomPlaceholder={renderCustomPlaceholder}
        renderListItem={renderListItem}
        orderedLearningObjectives={orderedLearningObjectives}
        reorderLearningObjectives={reorderLearningObjectives}
        shouldShowEmptyState={shouldShowEmptyState}
        shouldShowNestedRows={shouldShowNestedRows}
        shouldShowLoadingState={shouldShowLoadingState}
      />
    );
  }

  return (
    <LearningObjectivesList
      handleActionItemClick={handleActionItemClick}
      handleKebabMenuItemClick={handleKebabMenuItemClick}
      isReadOnly={isReadOnly}
      isLearningObjectivesDisplayed={isLearningObjectivesDisplayed}
      isSourceMaterialProcessed={isSourceMaterialProcessed}
      renderCustomPlaceholder={renderCustomPlaceholder}
      renderListItem={renderListItem}
      orderedLearningObjectives={orderedLearningObjectives}
      reorderLearningObjectives={reorderLearningObjectives}
      shouldShowEmptyState={shouldShowEmptyState}
      shouldShowNestedRows={shouldShowNestedRows}
      shouldShowLoadingState={shouldShowLoadingState}
    />
  );
};

LearningObjectives.propTypes = {
  defaultObjectiveableId: PropTypes.number,
  draftState: PropTypes.oneOf([null, 'editing', 'in_review', 'approved']),
  isCourseDraft: PropTypes.bool,
};

export default LearningObjectives;
